/* eslint-disable no-console */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { createSelector } from 'reselect';
import { generateObjectId, joyDocHelper, tableHelper } from '@builttocreate/engine-utils';

import { removeOrphanedFieldsFromJoydoc } from '@builttocreate/engine-utils/dist/joyDocHelper';
import File from './form/File';

import { ThemeProvider } from '../theme/ThemeContext';
import getThemeObject from '../theme/getThemeObject';
import { DecoratorConfigProvider, DEFAULT_DECORATOR_CONFIG } from './DecoratorConfigContext';

import { Red } from '../constants/Colors';
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import PageLayoutModes from '../constants/PageLayoutModes';
import PageDisplayTypes from '../constants/PageDisplayTypes';
import FieldPresentationModes from '../constants/FieldPresentationModes';
import FieldModes from '../constants/FieldModes';
import FileViews from '../constants/FileViews';
import DefaultFieldListOptions from '../constants/DefaultFieldListOptions';
import ChangelogTypes from '../constants/ChangelogTypes';
import FieldTypes from '../constants/FieldTypes';
import { formulas } from './form/formulas';

import validateDocSchema from '../lib/validateSchema';
import SchemaValidationErrorView from './misc/SchemaValidationErrorView';
import { validateLicense } from '../lib/licenseValidator.js';


import {
  getFieldAndFieldPositionFromCombinedPayloadForCreation,
  getCleanedDocSelector,
  generateAndPopulateMobileViewForFile,
  recalculatePositionCoordinatesForPageFields,
  separateFieldAndFieldPositionChangesForUpdate,
  getDuplicatedPageAndChangelogsForDocument,
  getDuplicatedPageWithoutValuesForDocument,
  normalizePageActionProperties,
  formatPageForMobileView,
  getDefaultJoyDocPage,
  updateConditionalLogicFieldReferences,
  getFileViewSelector,
  getActiveFileSelector,
  getFileViews,
  generateId,
  updateAllFieldPositionAssociatedWithField,
} from '../lib/documentHelper';

import { cleanRow, syncTableFieldColumnIdChangeInAllViewFieldPositions } from '../lib/tableHelper';
import { syncCollectionFieldColumnIdChange } from '../lib/collectionHelper';

const HeaderTarget = 'header';
const FooterTarget = 'footer';

import { getFieldSettingsConfigForField } from '../lib/settingsHelper';

const {
  getPageOrder,
  sortPages,
} = joyDocHelper;

const {
  getRowOrder,
  getColumnOrder,
  handleInsertTableRow,
  handleDuplicateTableRow,
  handleMoveTableRowUp,
  handleMoveTableRowDown,
} = tableHelper;

const StyledError = styled.div`
  border: 2px solid ${Red};
  color: ${Red};
  font-weight: bold;
  padding: 8px;
`;

/**
 * Custom shouldForwardProp function for styled-components v6 with React 19.
 * Filters out custom styled-component props from being passed to the DOM.
 * Uses @emotion/is-prop-valid as the base validation.
 */
const shouldForwardProp = (propName, target) => {
  // For custom components (functions/classes), forward all props
  if (typeof target === 'function') {
    return true;
  }
  // For string targets (DOM elements like 'div', 'span', 'svg'), use isPropValid
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  // Default: forward all props for unknown target types
  return true;
};

class JoyDoc extends PureComponent {

  static propTypes = {

    mode: PropTypes.string,
    view: PropTypes.string,
    theme: PropTypes.object,

    width: PropTypes.number,
    height: PropTypes.number,

    //Externally control initially active page and field
    initialPageId: PropTypes.string,

    //Important for Lazy Loading
    lazyload: PropTypes.bool,

    features: PropTypes.object,
    fieldOptions: PropTypes.array,
    fieldSettings: PropTypes.object,
    identifiers: PropTypes.object,

    doc: PropTypes.object,

    onChange: PropTypes.func,
    onCaptureAsync: PropTypes.func,
    onUploadAsync: PropTypes.func,
    onError: PropTypes.func,

    onFocus: PropTypes.func,
    onBlur: PropTypes.func,


    validateSchema: PropTypes.bool,


    license: PropTypes.string,

    navigator: PropTypes.object,

    decoratorConfig: PropTypes.object,

  }

  static defaultProps = {
    mode: FieldModes.edit,
    fieldSettings: {},

    features: {
      formulas: false,
      readableIds: false,
      repeatingHeaderAndFooter: false,

      //Disables internal joyfill schema validation
      validateSchema: false,

      //Optional for formula sandbox mode
      formulasSandbox: false,

      //Enables field measurement rendering for fields that support it.
      autoResizeFields: false,
    },

    width: 816,
    height: 1056,

    decoratorConfig: {
      visibleLimitInFields: 2,
      visibleLimitInRows: 1,
    },
  }

  constructor(props) {
    super(props);

    const { doc, view, license } = props;

    let nextDoc = getCleanedDocSelector({ doc });
    nextDoc = normalizePageActionProperties(nextDoc);
    const validationError = this.handleSchemaValidationError(nextDoc);

    // Resolve formulas on initial load
    const formulaResolution = resolveFormulasAndGenerateChangelogs(nextDoc, null, null, null);

    // Use the resolved doc that contains updated field values
    nextDoc = formulaResolution.doc;

    const activeFile = getActiveFileSelector({ doc: nextDoc });

    this.state = {
      view: view || FileViews.desktop,
      doc: nextDoc,
      activeFileId: activeFile?._id,
      validationError,
      licenseValidation: null, // Will be set asynchronously
      measurements: { desktop: {}, mobile: {} }, // Will be set asynchronously: { pageId: { fieldPositionId: { height: number }, ... }, ... }
      updateTimestamps: {}, // Will be set asynchronously: { pageId: { fieldPositionId: timestamp }, ... }
    };

    if (license) {
      // Handle license validation asynchronously
      this.handleLicenseValidation(license).then(licenseValidation => {
        this.setState({ licenseValidation });
      });
    }

  }

  componentDidUpdate(prevProps) {

    const { doc, view, license, features } = this.props;
    const { activeFileId } = this.state;

    if (doc !== prevProps.doc) {

      let nextDoc = getCleanedDocSelector({ doc });
      const validationError = this.handleSchemaValidationError(nextDoc);

      // Resolve formulas when doc changes
      const formulaResolution = resolveFormulasAndGenerateChangelogs(nextDoc, null, null, null);

      // Use the resolved doc that contains updated field values
      nextDoc = formulaResolution.doc;

      const activeFile = getActiveFileSelector({ doc: nextDoc, activeFileId });

      this.setState({
        doc: nextDoc,
        activeFileId: activeFile?._id,
        validationError,
      });

    }

    if (view !== prevProps.view) {
      this.setState({ view });
    }

    /**
     * Very important to reset measurements and updateTimestamps when autoResizeFields feature is toggled.
     * 
     * This is because when the feature is toggled, we need to recalculate the measurements and updateTimestamps
     * for all fields in the document. 
     */
    if (features?.autoResizeFields !== prevProps.features?.autoResizeFields) {
      this.setState({ measurements: { desktop: {}, mobile: {} }, updateTimestamps: {} });
    }

    if (license && license !== prevProps.license) {

      this.handleLicenseValidation(license).then(licenseValidation => {
        this.setState({ licenseValidation });
      });

    }

  }

  render() {

    const { theme, mode, initialPageId, fieldOptions, fieldSettings, height, width, lazyload, identifiers, onUploadAsync, license, features, useReadableIds, decoratorConfig } = this.props;
    const { doc, activeFileId, view, validationError, licenseValidation, measurements, updateTimestamps } = this.state;

    const file = getActiveFileSelector({ doc, activeFileId });
    const fileView = file ? getFileViewSelector({ doc, activeFileId, view }) : null;

    const fileViews = getFileViews({
      file,
      pages: file?.pages || []
    });

    if (!file || !fileView || !fileView.pages || fileView.pages.length < 1 || !doc.files || !doc.fields) {
      return (
        <StyledError>Invalid JoyDoc JSON Pattern.</StyledError>
      );
    }

    if (validationError) {
      return (
        <SchemaValidationErrorView schemaError={validationError} />
      );
    }

    return !fileView ? null : (
      <DecoratorConfigProvider value={{ ...DEFAULT_DECORATOR_CONFIG, ...decoratorConfig }}>
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
          <ThemeProvider value={getThemeObject(theme)}>
            <File

            fields={doc.fields}

            view={view}

            mode={mode}
            width={width}
            height={height}
            enableLazyLoadFields={lazyload}
            features={features}

            id={activeFileId}
            initialPageId={initialPageId}

            {...fileView}

            views={fileViews}
            name={doc.name}
            fieldOptions={fieldOptions || DefaultFieldListOptions}
            fieldSettings={fieldSettings}
            identifiers={identifiers}
            doc={doc}

            onFileChange={this.handleFileChange}
            onFileUpload={this.handleFileUpload}
            onCapture={this.handleCapture}
            onDocumentChange={this.handleDocumentChange}

            onPageCreate={this.handlePageCreate}
            onPageUploadDelete={this.handlePageUploadDelete}
            onPageUpload={this.handlePageUpload}
            onPageChange={this.handlePageChange}
            onPageDelete={this.handlePageDelete}
            onPageDuplicate={this.handlePageDuplicate}
            onPageMoveLeft={this.handlePageMoveLeft}
            onPageMoveRight={this.handlePageMoveRight}

            onFieldCreate={this.handleFieldCreate}
            onFieldChange={this.handleFieldChange}
            onFieldDuplicate={this.handleFieldDuplicate}
            onFieldPositionChange={this.handleFieldPositionChange}
            onFieldPositionDuplicate={this.handleFieldPositionDuplicate}
            onFieldRowAddBelow={this.handleFieldRowAddBelow}
            onFieldRowDuplicate={this.handleFieldRowDuplicate}
            onFieldRowMoveUp={this.handleFieldRowMoveUp}
            onFieldRowMoveDown={this.handleFieldRowMoveDown}
            onFieldRowCellChange={this.handleFieldRowCellChange}
            onFieldRowCellBatchChange={this.handleFieldRowCellBatchChange}
            onFieldRowCellUpload={this.handleFieldRowCellUpload}
            onFieldRowCellBatchUpload={this.handleFieldRowCellBatchUpload}
            onFieldRowDelete={this.handleFieldRowDelete}
            onFieldColumnDelete={this.handleFieldColumnDelete}
            onFieldDelete={this.handleFieldDelete}
            onFieldLayoutsChange={this.handleFieldPositionLayoutsChange}

            updateTimestamps={updateTimestamps}
            measure={features?.autoResizeFields}
            measurements={measurements[view] || {}}
            onMeasurementsCompleted={this.handleMeasurementsCompleted}

            onFieldFileUpload={this.handleFieldFileUpload}
            onFieldFileDelete={this.handleFieldFileDelete}
            onFieldFileClick={this.handleFieldFileClick}

            onViewChange={this.handleViewChange}
            onToggleViewSync={this.handleToggleViewSync}
            onFocus={this.handleFieldFocus}
            onDecoratorClick={this.handleDecoratorAction}
            onSelect={this.handleFieldSelect}
            onBlur={this.handleFieldBlur}
            onPageFocus={this.handlePageFocus}
            onPageBlur={this.handlePageBlur}
            onUploadAsync={onUploadAsync}
            documentId={doc._id}
            documentIdentifier={doc.identifier}
            licenseValidation={licenseValidation}
            navigator={this.props.navigator}
            />
          </ThemeProvider>
        </StyleSheetManager>
      </DecoratorConfigProvider>
    );

  }

  /**
   * @private
   */
  handleSchemaValidationError = (doc) => {

    const { onError, features, validateSchema } = this.props;

    /**
     * Disables internal joyfill schema validation.
     * 
     * We use the "===" to specifically check for false. If validateSchema is not
     * specifically set to false, we will default to true and validate the schema.
     */
    if (features?.validateSchema === false) return;

    const isSchemaNotValid = validateDocSchema(doc);

    if (doc && isSchemaNotValid) {
      if (onError) onError(isSchemaNotValid);
      return isSchemaNotValid;
    }

  }

  /**
   * @private
   */
  handleLicenseValidation = async (license) => {

    const { onError } = this.props;

    /**
     * If no license is provided, we don't validate (optional license)
     */

    const validation = await validateLicense(license);

    if (!validation.isValid && onError) {
      onError({
        code: 'LICENSE_VALIDATION_ERROR',
        message: validation.error,
        type: 'license'
      });
    }

    return validation;

  }

  /**
   * @private
   */
  handleFieldFocus = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier, parentPath) => {

    const { doc } = this.state;
    const { onFocus, } = this.props;

    const params = {
      sdk: 'js',
      v: 1,
      type: ChangelogTypes.fieldPositionFocus,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldId,
      fieldPositionId,
      fieldIdentifier
    };

    if (rowId) params.fieldRowId = rowId;
    if (columnId) params.fieldColumnId = columnId;
    if (columnIdentifier) params.fieldColumnIdentifier = columnIdentifier;

    if (parentPath) params.parentPath = parentPath;

    if (onFocus) onFocus(params, e);

  };

  /**
   * @private
   */
  handleDecoratorAction = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, action, rowId, parentPath) => {

    const { doc } = this.state;
    const { onFocus } = this.props;

    const params = {
      sdk: 'js',
      v: 1,
      type: action,
      target:action,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldId,
      fieldPositionId,
      fieldIdentifier
    };

    if (rowId) params.rowIds = [rowId];
    if (parentPath) params.parentPath = parentPath;

    if (onFocus) onFocus(params);

  };

  /**
   * @private
   */
  handleFieldSelect = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, columnIdentifier,) => {

    const { doc } = this.state;
    const { onSelect } = this.props;

    const params = {
      sdk: 'js',
      v: 1,
      type: ChangelogTypes.fieldPositionSelect,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldId,
      fieldPositionId,
      fieldIdentifier
    };

    if (rowId) params.fieldRowId = rowId;
    if (columnId) params.fieldColumnId = columnId;
    if (columnIdentifier) params.fieldColumnIdentifier = columnIdentifier;

    if (onSelect) onSelect(params);

  };

  /**
   * @private
   */
  handleFieldBlur = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier, parentPath) => {

    const { doc } = this.state;
    const { onBlur } = this.props;

    const params = {
      sdk: 'js',
      v: 1,
      type: ChangelogTypes.fieldPositionBlur,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldId,
      fieldPositionId,
      fieldIdentifier
    };

    if (rowId) params.fieldRowId = rowId;
    if (columnId) params.fieldColumnId = columnId;
    if (columnIdentifier) params.fieldColumnIdentifier = columnIdentifier;
    if (parentPath) params.parentPath = parentPath;

    if (onBlur) onBlur(params, e);

  };

  /**
   * @private
   * Fires when a new page becomes active (page.focus event).
   */
  handlePageFocus = (fileId, pageObject) => {
    const { doc } = this.state;
    const { onFocus } = this.props;

    if (onFocus) {
      onFocus({
        sdk: 'js',
        v: 1,
        type: ChangelogTypes.pageFocus,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        page: pageObject,
      });
    }
  };

  /**
   * @private
   * Fires when a page loses focus (page.blur event).
   */
  handlePageBlur = (fileId, pageObject) => {
    const { doc } = this.state;
    const { onBlur } = this.props;

    if (onBlur) {
      onBlur({
        sdk: 'js',
        v: 1,
        type: ChangelogTypes.pageBlur,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        page: pageObject,
      });
    }
  };

  /**
   * @private
   */
  handleToggleViewSync = (view) => {

    const { onChange } = this.props;
    const { doc, activeFileId } = this.state;

    const nextDoc = { ...doc, files: [...doc.files] };
    const fileIndex = nextDoc.files.findIndex((file) => file._id === activeFileId);
    nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], views: nextDoc.files[fileIndex].views ? [...nextDoc.files[fileIndex].views] : [] };

    /**
     * Step 1: Generate and add the view if the view type doesn't currently exist.
     */
    if (!nextDoc.files[fileIndex].views || nextDoc.files[fileIndex].views.findIndex((v) => v.type === view) === -1) {
      nextDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(nextDoc.files[fileIndex]);
    }

    /**
     * Step 2: Remove the view type it exists.
     */
    else {
      const viewIndex = nextDoc.files[fileIndex].views.findIndex((v) => v.type === view);
      nextDoc.files[fileIndex].views.splice(viewIndex, 1);
    }

    if (onChange) {
      onChange(
        [
          {
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fileUpdate,
            _id: doc._id,
            identifier: doc.identifier,
            fileId: nextDoc.files[fileIndex]._id,
            change: {
              views: [...nextDoc.files[fileIndex].views]
            },
            createdOn: new Date().getTime()
          }
        ],
        nextDoc
      );
    }

    this.setState({ doc: nextDoc, updateTimestamps: {}, measurements: { desktop: {}, mobile: {} } });

  }

  /**
   * @private
   */
  handleViewChange = (view) => {
    this.setState({ view });
  }

  /**
   * @private
   */
  handleFileChange = (fileId, changes) => {

    const { onChange } = this.props;
    const { doc } = this.state;

    const nextDoc = { ...doc, files: [...doc.files] };
    const fileIndex = doc.files.findIndex((file) => file._id === fileId);

    /**
     * Keep primary file.name in sync with document.name since we are
     * only supporting single files right now.
     */
    if (changes.name) nextDoc.name = changes.name;
    nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], ...changes };

    this.setState({ doc: nextDoc });

    if (onChange) {
      onChange(
        [
          {
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fileUpdate,
            _id: doc._id,
            identifier: doc.identifier,
            fileId: nextDoc.files[fileIndex]._id,
            change: changes,
            createdOn: new Date().getTime()
          }
        ],
        nextDoc
      );
    }

  }

  /**
   * @private
   */
  handleDocumentChange = (updatedDoc) => {

    const { onChange } = this.props;
    const { doc } = this.state;

    let finalDoc = updatedDoc;
    const changelogs = [];

    // Add the main document update changelog
    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.documentUpdate,
      _id: doc._id,
      identifier: doc.identifier,
      change: updatedDoc,
      createdOn: new Date().getTime()
    });

    // Resolve formulas on document update
    const formulaResolution = resolveFormulasAndGenerateChangelogs(updatedDoc, null, null, null);

    // Apply any field updates from formula resolution
    finalDoc.fields = formulaResolution.doc.fields;
    // Add formula-driven changelogs
    changelogs.push(...formulaResolution.changelogs);

    const activeFile = getActiveFileSelector({ doc: finalDoc });

    this.setState({
      doc: finalDoc,
      activeFileId: activeFile?._id
    });

    if (onChange) {
      onChange(changelogs, finalDoc);
    }

  }

  /**
   * @private
   */
  handleFileUpload = async (fileId, fileUpload) => {

    const { onUploadAsync, onChange } = this.props;
    const { doc } = this.state;

    const nextFileUpload = Array.isArray(fileUpload) ? fileUpload : [fileUpload];

    const uploadResult = await onUploadAsync(
      {
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.fileUpdate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId: doc._id,
      },
      nextFileUpload
    );

    const urlObjects = Array.isArray(uploadResult) ? uploadResult : [uploadResult];

    if (!urlObjects) return;

    const nextDoc = { ...doc, files: [...doc.files] };
    const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
    nextDoc.files[fileIndex] = {
      ...nextDoc.files[fileIndex],
      pages: [...nextDoc.files[fileIndex].pages],
      pageOrder: [...(nextDoc.files[fileIndex].pageOrder || [])],
      views: nextDoc.files[fileIndex].views
        ? nextDoc.files[fileIndex].views.map((v) => ({
            ...v,
            pages: v.pages ? [...v.pages] : [],
            pageOrder: v.pageOrder ? [...v.pageOrder] : [],
          }))
        : [],
    };

    urlObjects.forEach((urlObject, urlIndex) => {

      const { url, fileName, filePath } = urlObject;

      const newPage = getDefaultJoyDocPage({ displayType: PageDisplayTypes.pdf });

      /**
       * Step 1: Handle primary view
       */
      const pageOrderId = nextDoc.files[fileIndex].pageOrder[urlIndex];

      if (!pageOrderId) {

        const newPrimaryPage = { ...newPage, backgroundImage: url };

        if (filePath) newPrimaryPage.filePath = filePath;
        if (fileName) newPrimaryPage.fileName = fileName;

        nextDoc.files[fileIndex].pages.push(newPrimaryPage);
        nextDoc.files[fileIndex].pageOrder[urlIndex] = newPrimaryPage._id;

      } else {

        const pageIndex = nextDoc.files[fileIndex].pages.findIndex((page) => page._id === pageOrderId);
        const originalPage = nextDoc.files[fileIndex].pages[pageIndex];

        const nextPage = {
          ...nextDoc.files[fileIndex].pages[pageIndex],
          layout: PageLayoutModes.float,
          presentation: FieldPresentationModes.transparent,
          backgroundImage: url,
          cols: 816,
          rowHeight: 1
        };

        if (filePath) nextPage.filePath = filePath;
        if (fileName) nextPage.fileName = fileName;

        const layouts = recalculatePositionCoordinatesForPageFields(
          [...nextPage.fieldPositions],
          nextPage.cols,
          nextPage.rowHeight,
          originalPage.cols,
          originalPage.rowHeight
        );

        const layoutLookup = {};
        layouts.forEach((layout) => layoutLookup[layout._id] = layout);

        nextPage.fieldPositions = nextPage.fieldPositions.map((fieldPosition) => {
          return {
            ...fieldPosition,
            ...layoutLookup[fieldPosition._id]
          };
        });

        nextDoc.files[fileIndex].pages[pageIndex] = nextPage;

      }

      /**
       * Step 2: Handle alternative views
       */
      nextDoc.files[fileIndex].views = nextDoc.files[fileIndex]?.views?.length > 0 ?
        nextDoc.files[fileIndex].views.map((view) => {

          const nextView = { ...view, pages: [...(view.pages || [])], pageOrder: [...(view.pageOrder || [])] };

          const viewPageOrderId = nextView.pageOrder[urlIndex];

          if (!viewPageOrderId) {

            const newViewPage = formatPageForMobileView(newPage);

            nextView.pages.push(newViewPage);
            nextView.pageOrder[urlIndex] = newViewPage._id;

          }

          return nextView;

        }) : [];

    });

    this.setState({ doc: nextDoc });

    if (onChange) {
      onChange(
        [
          {
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fileUpdate,
            _id: doc._id,
            identifier: doc.identifier,
            fileId: nextDoc.files[fileIndex]._id,
            change: { pages: [...nextDoc.files[fileIndex].pages] },
            createdOn: new Date().getTime()
          }
        ],
        nextDoc
      );
    }

  }

  /**
   * @private
   */
  handleCapture = async () => {
    const { onCaptureAsync } = this.props;
    const response = await onCaptureAsync();

    return response;
  }

  /**
   * @private
   */
  handlePageUploadDelete = async (fileId, pageId) => {
    this.handlePageChange(fileId, pageId, { fileName: '', filePath: '', backgroundImage: '' });
  }

  /**
   * @private
   */
  handlePageUpload = async (fileId, pageId, fileUpload) => {

    const { onUploadAsync } = this.props;
    const { doc } = this.state;

    const nextFileUpload = Array.isArray(fileUpload) ? fileUpload : [fileUpload];

    const response = await onUploadAsync(
      {
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.pageUpdate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId
      },
      nextFileUpload
    );

    if (!response) return;

    const nextResponse = Array.isArray(response) ? response : [response];

    const { url, fileName, filePath } = nextResponse[0];

    const pageChanges = {};
    pageChanges.cols = 816;
    pageChanges.rowHeight = 1;
    pageChanges.backgroundImage = url;
    pageChanges.layout = PageLayoutModes.float;
    pageChanges.presentation = FieldPresentationModes.transparent;

    if (fileName) pageChanges.fileName = fileName;
    if (filePath) pageChanges.filePath = filePath;

    this.handlePageChange(fileId, pageId, pageChanges);

  }

  /**
   * @private
   */
  handlePageCreate = (fileId) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handlePageCreate(doc, view, fileId, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handlePageChange = (fileId, pageId, changes) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handlePageChange(doc, view, fileId, pageId, changes, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handlePageDuplicate = (fileId, pageId, copyMode) => {

    const { onChange, features } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handlePageDuplicate(doc, view, fileId, pageId, onChange, features?.readableIds, copyMode);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handlePageDelete = (fileId, pageId) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    /**
     * Step 1: Auto select the next available page since the current one got removed.
     */
    const fileIndex = doc.files.findIndex((file) => file._id === fileId);
    const sortedPages = sortPages(doc.files[fileIndex].pages, doc.files[fileIndex].pageOrder);

    //Cannot delete last page in files
    if (sortedPages.length < 2) return;

    /**
     * Step 2: Handle page delete from doc and call onChange
     */
    const nextDoc = handlePageDelete(doc, view, fileId, pageId, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handlePageMoveLeft = (fileId, pageId) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handlePageMoveLeft(doc, view, fileId, pageId, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handlePageMoveRight = (fileId, pageId) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handlePageMoveRight(doc, view, fileId, pageId, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handleFieldCreate = (fileId, pageId, newField) => {

    const { onChange, features } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handleFieldCreate(doc, view, fileId, pageId, newField, onChange, features?.readableIds);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handleFieldDuplicate = (pageId, fieldId, fieldPositionId) => {
    const { onChange, features } = this.props;
    const { doc, view, activeFileId: fileId } = this.state;

    const nextDoc = handleFieldDuplicate(doc, view, fileId, pageId, fieldId, fieldPositionId, onChange, features?.readableIds);

    this.setState({ doc: nextDoc });
  };

  /**
   * @private
   */
  handleFieldPositionDuplicate = (pageId, fieldId, fieldPositionId) => {
    const { onChange, features } = this.props;
    const { doc, view, activeFileId: fileId } = this.state;

    const nextDoc = handleDuplicateFieldPositionOnly(doc, view, fileId, pageId, fieldId, fieldPositionId, onChange);

    this.setState({ doc: nextDoc });
  }

  /**
   * @private
   */
  handleFieldPositionChange = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, changes, collectionFieldParams) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handleFieldPositionChange(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, changes, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handleFieldChange = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, changes, collectionFieldParams, eventType, localTimezone) => {

    const { onChange } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const nextDoc = handleFieldChange(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, changes, collectionFieldParams, eventType, onChange, localTimezone);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   */
  handleFieldRowCellChange = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change, timezone) => {

    const { onChange } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const nextDoc = handleTableFieldRowCellChange(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change, onChange, timezone);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   * Applies multiple cell changes for a single row in one atomic setState.
   * @param {Object} cellChanges - Map of columnId -> value
   */
  handleFieldRowCellBatchChange = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, cellChanges, timezone) => {

    const { onChange } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const { nextDoc, changelogs } = buildTableFieldRowCellBatchChangeResult(
      doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, cellChanges, timezone
    );

    if (onChange) onChange(changelogs, nextDoc);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   * @param {Array} fileUploads raw file from Dropzone (new files to add)
   */
  handleFieldRowCellUpload = async (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, fileUploads, loadingCallback, errorCallback) => {

    const { onUploadAsync, onChange, fieldSettings } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const field = getFieldByIdSelector({ fields: doc.fields, fieldId });
    const fieldConfig = getFieldSettingsConfigForField(field, fieldSettings);

    const targetColumn = field.tableColumns.find((column) => column._id === columnId);
    const isMulti = targetColumn?.multi || false;

    const { onFileUploadAsync } = fieldConfig || {};
    const uploadAsyncHandler = onFileUploadAsync || onUploadAsync;

    let uploadResult;
    let formattedUrlObjects;

    try {

      loadingCallback(true);

      uploadResult = await uploadAsyncHandler(
        {
          target: ChangelogTypes.fieldUpdate,
          _id: doc._id,
          identifier: doc.identifier,
          fileId,
          pageId,
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          rowId,
          columnId,
          multi: isMulti,
        },
        fileUploads,
      );

      const urlObjects = Array.isArray(uploadResult) ? uploadResult : [uploadResult];

      formattedUrlObjects = urlObjects.map((urlObject) => {
        const { url, fileName, filePath } = urlObject;
        return { _id: generateObjectId(), url, fileName, filePath };
      });

      loadingCallback(false);

    }
    catch (error) {
      if (errorCallback) errorCallback(true);
    }

    if (!formattedUrlObjects
      || (formattedUrlObjects && formattedUrlObjects.length === 0)
      || !formattedUrlObjects[0].url) return;

    const nextRows = [...field.value];
    const rowIndex = nextRows.findIndex((row) => row._id === rowId);
    const targetRow = nextRows[rowIndex];

    let nextUrlObjects = [...formattedUrlObjects];

    const existingValue = targetRow.cells[columnId];

      if (isMulti) {
        nextUrlObjects = Array.isArray(existingValue) ? [...existingValue, ...nextUrlObjects] : nextUrlObjects;
      } else {
        nextUrlObjects = [nextUrlObjects[0]];
      }

    const nextDoc = handleTableFieldRowCellChange(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, nextUrlObjects, onChange);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   * Uploads files for multiple image columns in a single row sequentially,
   * then applies all resulting URL changes in one atomic setState.
   * @param {Object} columnUploads - Map of columnId -> File[]
   */
  handleFieldRowCellBatchUpload = async (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer) => {

    const { onUploadAsync, onChange, fieldSettings } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const field = getFieldByIdSelector({ fields: doc.fields, fieldId });
    const fieldConfig = getFieldSettingsConfigForField(field, fieldSettings);

    const { onFileUploadAsync } = fieldConfig || {};
    const uploadAsyncHandler = onFileUploadAsync || onUploadAsync;

    const cellChanges = {};

    if (batchedCells && typeof batchedCells === 'object') {
      Object.assign(cellChanges, batchedCells);
    }

    const columnIds = Object.keys(columnUploads);

    try {

      loadingCallback(true);

      for (let i = 0; i < columnIds.length; i++) {

        const columnId = columnIds[i];
        const uploadEntry = columnUploads[columnId];
        const fileUploads = uploadEntry.files || uploadEntry;
        const baseExistingValue = uploadEntry.existingValue;

        const targetColumn = field.tableColumns.find((column) => column._id === columnId);
        const isMulti = targetColumn?.multi || false;

        const uploadResult = await uploadAsyncHandler(
          {
            target: ChangelogTypes.fieldUpdate,
            _id: doc._id,
            identifier: doc.identifier,
            fileId,
            pageId,
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            rowId,
            columnId,
            multi: isMulti,
          },
          fileUploads,
        );

        const urlObjects = Array.isArray(uploadResult) ? uploadResult : [uploadResult];

        const formattedUrlObjects = urlObjects.map((urlObject) => {
          const { url, fileName, filePath } = urlObject;
          return { _id: generateObjectId(), url, fileName, filePath };
        });

        if (!formattedUrlObjects || formattedUrlObjects.length === 0 || !formattedUrlObjects[0].url) continue;

        const nextRows = [...field.value];
        const rowIndex = nextRows.findIndex((row) => row._id === rowId);
        const targetRow = nextRows[rowIndex];
        const existingValue = cellChanges[columnId] || (baseExistingValue !== undefined ? baseExistingValue : targetRow.cells[columnId]);

        let nextUrlObjects = [...formattedUrlObjects];

        if (isMulti) {
          nextUrlObjects = Array.isArray(existingValue) ? [...existingValue, ...nextUrlObjects] : nextUrlObjects;
        } else {
          nextUrlObjects = [nextUrlObjects[0]];
        }

        cellChanges[columnId] = nextUrlObjects;

      }

      loadingCallback(false);

    }
    catch (error) {
      if (errorCallback) errorCallback(true);
    }

    if (Object.keys(cellChanges).length === 0) return;

    const { nextDoc, changelogs } = buildTableFieldRowCellBatchChangeResult(
      doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, cellChanges, timezone, signer
    );

    if (onChange) onChange(changelogs, nextDoc);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   */
  handleFieldRowAddBelow = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    const { onChange } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const nextDoc = handleTableFieldRowAddBelow(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   */
  handleFieldRowDuplicate = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex) => {

    const { onChange } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const nextDoc = handleTableFieldRowDuplicate(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex, onChange);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   */
  handleFieldRowMoveUp = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handleTableFieldRowMoveUp(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handleFieldRowMoveDown = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handleTableFieldRowMoveDown(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handleFieldRowDelete = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    const { onChange } = this.props;
    const { doc, view, updateTimestamps } = this.state;

    const nextDoc = handleTableFieldRowDelete(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   */
  handleFieldColumnDelete = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, columnId) => {

    const { onChange, view } = this.props;
    const { doc, updateTimestamps } = this.state;

    const nextDoc = handleTableFieldColumnDelete(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, columnId, onChange);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   */
  handleFieldFileClick = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, fileData, fileIndex, extraParams = {}) => {

    const { fieldSettings } = this.props;
    const { doc } = this.state;

    const field = getFieldByIdSelector({ fields: doc.fields, fieldId });
    const fieldConfig = getFieldSettingsConfigForField(field, fieldSettings);

    const { onFileClick } = fieldConfig || {};

    if (onFileClick) {

      const params = {
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        fieldId,
        fieldIdentifier,
        fieldPositionId,
        ...extraParams
      };


      onFileClick(
        params,
        fileData,
      );

    }

  }

  /**
   * @private
   */
  handleFieldFileDelete = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, fileData, fileIndex, extraParams = {}) => {

    const { fieldSettings } = this.props;
    const { doc } = this.state;

    const field = getFieldByIdSelector({ fields: doc.fields, fieldId });
    const fieldConfig = getFieldSettingsConfigForField(field, fieldSettings);

    const { onFileDelete } = fieldConfig || {};

    if (onFileDelete) {
      const params = {
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        fieldId,
        fieldIdentifier,
        fieldPositionId,
        ...extraParams
      };

      onFileDelete(
        params,
        fileData,
      );
    }

  }

  /**
   * @private
   * @param {Array} fileUploads raw file from Dropzone (new files to add)
   */
  handleFieldFileUpload = async (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, fileUploads, loadingCallback, errorCallback) => {

    const { onUploadAsync, onChange, fieldSettings } = this.props;

    const { doc, view, updateTimestamps } = this.state;

    const field = getFieldByIdSelector({ fields: doc.fields, fieldId });
    const fieldConfig = getFieldSettingsConfigForField(field, fieldSettings);
    const isMulti = field.multi || false;

    const { onFileUploadAsync } = fieldConfig || {};
    const uploadAsyncHandler = onFileUploadAsync || onUploadAsync;

    let uploadResult;
    let formattedUrlObjects;

    try {

      loadingCallback(true);

      uploadResult = await uploadAsyncHandler(
        {
          target: ChangelogTypes.fieldUpdate,
          _id: doc._id,
          identifier: doc.identifier,
          fileId,
          pageId,
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          multi: isMulti
        },
        /**
         * Notice: fileUploads will be undefined if the developer has disabled the default system upload
         * via the field settings config. If they have disabled it then they are responsible  for retrieving
         * the file uploads from the user and returing us the properly formatted upload result.
         */
        fileUploads,
      );

      const urlObjects = Array.isArray(uploadResult) ? uploadResult : [uploadResult];

      formattedUrlObjects = urlObjects.map((urlObject) => {

        const { url, fileName, filePath } = urlObject;

        return { _id: generateObjectId(), url, fileName, filePath };

      });

      loadingCallback(false);

    }
    catch (error) {
      if (errorCallback) errorCallback(true);
    }

    if (!formattedUrlObjects
      || (formattedUrlObjects && formattedUrlObjects.length === 0)
      || !formattedUrlObjects[0].url) return;

    const nextDoc = handleFieldFileUpload(doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, formattedUrlObjects, onChange);

    this.setState({ doc: nextDoc, updateTimestamps: handleTargetFieldPositionUpdateTimestamp(updateTimestamps, pageId, fieldPositionId) });

  }

  /**
   * @private
   */
  handleFieldDelete = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId) => {

    const { onChange } = this.props;
    const { doc, view } = this.state;

    const nextDoc = handleFieldDelete(doc, view, fileId, pageId, fieldPositionId, onChange);

    this.setState({ doc: nextDoc });

  }

  /**
   * @private
   */
  handleFieldPositionLayoutsChange = (fileId, pageId, layouts) => {

    const { onChange, mode } = this.props;
    const { doc, view, measuringInProgress } = this.state;

    /**
     * Do not fire a layout change if not in edit mode or if measuring is currently in progress.
     */
    if (mode !== FieldModes.edit) return;

    const nextDoc = handleFieldPositionLayoutsChange(doc, view, fileId, pageId, layouts, onChange, measuringInProgress);

    this.setState({ doc: nextDoc });

  }

  handleMeasurementsCompleted = (fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rect) => {

    const { view } = this.state;

    // Use functional setState to batch updates and avoid race conditions
    this.setState(prevState => {

      const { measurements } = prevState;
      const activeView = view || FileViews.desktop;

      // Get existing page measurements or initialize empty object
      const pageMeasurements = measurements[activeView][pageId] || {};

      // Add or update the field position measurement
      const updatedPageMeasurements = {
        ...pageMeasurements,
        [fieldPositionId]: rect
      };

      // Return new measurements object
      return {
        measuringInProgress: true, //Prevent certain actions while measuring is in progress
        measurements: {
          ...measurements,
          [activeView]: {
            ...measurements[activeView],
            [pageId]: updatedPageMeasurements
          }
        }
      };
    });

    /**
     * Reset measuringInProgress state after 500ms. This is to prevent layout changes from firing 
     * before the measurement is complete. Once this is done, then we should allow layout changes to fire again.
     */
    if (this.measurementTimeout) clearTimeout(this.measurementTimeout);
    this.measurementTimeout = setTimeout(() => {
      this.setState({ measuringInProgress: false });
    }, 500);

  }
}

/**
 * Removes the target field position from the measurements object.
 * 
 * @param {Object} measurements 
 * @param {string} pageId 
 * @param {string} fieldPositionId 
 * @returns {Object} - Contains updated measurements with the target field position removed
 */
const handleTargetFieldPositionUpdateTimestamp = (updateTimestamps = {}, pageId, fieldPositionId) => {
  const timestamp = new Date().getTime();
  return {
    ...updateTimestamps,
    [pageId]: {
      ...(updateTimestamps[pageId] || {}),
      [fieldPositionId]: timestamp,
    },
  };
}

/**
 * Formula Resolution Handlers
 */

/**
 * Resolves formulas after field changes and generates changelogs for any fields
 * that were updated as a result of formula evaluation.
 * 
 * This handler is called after any field change to ensure all dependent fields
 * are recalculated based on the document's formula definitions.
 * 
 * @param {Object} doc - The document containing fields and formulas
 * @param {string} triggerFieldId - The field that triggered the formula resolution
 * @param {string} fileId - The file ID for changelog generation
 * @param {string} pageId - The page ID for changelog generation
 * @returns {Object} - Contains updated doc and array of changelogs for formula changes
 */
/**
 * @private
 */
const resolveFormulasAndGenerateChangelogs = (doc, triggerFieldId, fileId, pageId) => {
  const changelogs = [];

  // Skip if document has no formulas at the top level
  if (!doc.formulas || !Array.isArray(doc.formulas) || doc.formulas.length === 0) {
    return { doc, changelogs };
  }

  // Skip if no fields have formula applications
  const hasFieldsWithFormulas = doc.fields.some(field =>
    field.formulas && Array.isArray(field.formulas) && field.formulas.length > 0
  );

  if (!hasFieldsWithFormulas) {
    return { doc, changelogs };
  }

  try {
    // Store original field values before resolution
    const originalFieldValues = {};
    doc.fields.forEach(field => {
      originalFieldValues[field._id] = field.value;
    });

    // Resolve all formulas in the document
    const formulaResult = formulas.resolve(doc);

    if (formulaResult.status === 'valid' || formulaResult.status === 'warning') {
      const resolvedDoc = formulaResult.doc;

      // Check each field for changes caused by formula resolution
      resolvedDoc.fields.forEach((field, index) => {
        const originalValue = originalFieldValues[field._id];
        const newValue = field.value;

        // Generate changelog only if:
        // 1. The field value actually changed
        // 2. It's not the field that triggered the resolution
        if (field._id !== triggerFieldId && JSON.stringify(originalValue) !== JSON.stringify(newValue)) {
          // Update the field in the original doc
          doc.fields[index] = field;

          // Create a changelog for this formula-driven change
          changelogs.push({
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fieldUpdate,
            _id: doc._id,
            identifier: doc.identifier,
            fileId: field.file || fileId,
            pageId,
            fieldPositionId: null, // Formula changes don't have specific field position
            fieldId: field._id,
            fieldIdentifier: field.identifier,
            change: { value: newValue },
            createdOn: new Date().getTime(),
            formula: true // Mark this as a formula-driven change
          });
        }
      });
    }

    // Log warnings for debugging (non-blocking)
    if (formulaResult.warnings && formulaResult.warnings.length > 0) {
      console.warn('Formula resolution warnings:', formulaResult.warnings);
    }
  } catch (error) {
    // Log error but don't block the field update
    console.error('Error resolving formulas:', error);
  }

  return { doc, changelogs };
};

/**
 * Page Change Handlers
 */
/**
 * @private
 */
export const handlePageCreate = (doc, view, fileId, onChange) => {

  const newPage = getDefaultJoyDocPage();

  newPage.name = '';

  const nextDoc = { ...doc, files: [...doc.files] };
  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = {
    ...nextDoc.files[fileIndex],
    pages: [...nextDoc.files[fileIndex].pages],
    pageOrder: [...(nextDoc.files[fileIndex].pageOrder || [])],
  };

  const changelogs = [];

  /**
   * Step 1: Update primary view
   */
  nextDoc.files[fileIndex].pages.push(newPage);
  nextDoc.files[fileIndex].pageOrder.push(newPage._id);

  changelogs.push({
    sdk: 'js',
    v: 1,
    target: ChangelogTypes.pageCreate,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    change: {
      page: newPage,
      targetIndex: nextDoc.files[fileIndex].pageOrder.length,
    },
    createdOn: new Date().getTime()
  });

  /**
   * Step 2: Update alternative views
   */
  nextDoc.files[fileIndex].views = nextDoc.files[fileIndex]?.views?.length > 0 ?
    nextDoc.files[fileIndex].views.map((view) => {

      const nextView = { ...view, pages: [...(view.pages || [])], pageOrder: [...(view.pageOrder || [])] };
      const newViewPage = view.type === FileViews.mobile ? formatPageForMobileView(newPage) : newPage;

      nextView.pages.push(newViewPage);
      nextView.pageOrder.push(newViewPage._id);

      changelogs.push({
        sdk: 'js',
        v: 1,
        view: view.type,
        target: ChangelogTypes.pageCreate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        change: {
          page: newViewPage,
          targetIndex: nextView.pageOrder.length,
        },
        createdOn: new Date().getTime()
      });

      return nextView;
    }) : [];

  if (onChange) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

export const handlePageDuplicate = (doc, view, fileId, pageId, onChange, useReadableIds, copyMode) => {

  const duplicateFn = copyMode === 'without-values'
    ? getDuplicatedPageWithoutValuesForDocument
    : getDuplicatedPageAndChangelogsForDocument;

  const result = duplicateFn(doc, fileId, pageId, useReadableIds);

  if (onChange) {
    onChange(
      result.changelogs,
      result.doc
    );
  }

  return result.doc;

};

export const handlePageChange = (doc, view, fileId, pageId, changes, onChange) => {

  const nextDoc = { ...doc, files: [...doc.files] };
  const changelogs = [];
  const sharedPageChangeKeys = ['name', 'identifier', 'metadata'];
  const getSharedPageChanges = (targetChanges = {}) => {
    return sharedPageChangeKeys.reduce((acc, key) => {
      if (Object.prototype.hasOwnProperty.call(targetChanges, key)) {
        acc[key] = targetChanges[key];
      }
      return acc;
    }, {});
  };

  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = {
    ...nextDoc.files[fileIndex],
    pages: [...nextDoc.files[fileIndex].pages],
    pageOrder: [...(nextDoc.files[fileIndex].pageOrder || [])],
    views: nextDoc.files[fileIndex].views
      ? nextDoc.files[fileIndex].views.map((v) => ({ ...v, pages: v.pages ? [...v.pages] : v.pages, pageOrder: v.pageOrder ? [...v.pageOrder] : v.pageOrder }))
      : nextDoc.files[fileIndex].views,
  };
  const pageIndex = nextDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);
  const mobileViewIndex = nextDoc.files[fileIndex].views
    ? nextDoc.files[fileIndex].views.findIndex((targetView) => targetView.type === FileViews.mobile)
    : -1;
  const isEditingIndependentMobileView = view === FileViews.mobile && mobileViewIndex !== -1;
  const targetPages = isEditingIndependentMobileView
    ? nextDoc.files[fileIndex].views[mobileViewIndex].pages
    : nextDoc.files[fileIndex].pages;
  const targetPageIndex = targetPages.findIndex((page) => pageId === page._id);

  const activePage = targetPages[targetPageIndex];
  const nextPageChanges = { ...changes };

  if (!activePage) return nextDoc;

  /**
   * Step 1. Recalculate field positions when cols or rowHeight changes.
   *
   * Why do we do this?
   *
   * We do this so that fields don't get shifted around. For instance, if we have a page with 4 columns and a field
   * previously used 2 columns then it used 50% of the width of the form. So when we change the page to have 8 columns
   * we must update the field to now use 4 columns so that it maintains the 50% width it previusly used. This
   * prevents unexpcted visual changes in the fields on the page.
   */
  if (changes.cols || changes.rowHeight) {

    const layouts = recalculatePositionCoordinatesForPageFields(
      [...activePage.fieldPositions],
      changes.cols,
      changes.rowHeight,
      activePage.cols,
      activePage.rowHeight
    );

    const layoutLookup = {};
    layouts.forEach((layout) => layoutLookup[layout._id] = layout);

    nextPageChanges.fieldPositions = [...activePage.fieldPositions].map((field) => {
      return {
        ...field,
        ...layoutLookup[field._id]
      };
    });

  }

  /**
   * Step 2. Update target page for the active view.
   */
  targetPages[targetPageIndex] = { ...activePage, ...nextPageChanges };

  changelogs.push({
    sdk: 'js',
    v: 1,
    ...(isEditingIndependentMobileView ? { view } : {}),
    target: ChangelogTypes.pageUpdate,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    pageId,
    change: nextPageChanges,
    createdOn: new Date().getTime()
  });

  /**
   * Step 3. Keep shared page properties synchronized across views.
   */
  const sharedPageChanges = getSharedPageChanges(changes);

  if (Object.keys(sharedPageChanges).length > 0) {

    if (isEditingIndependentMobileView && pageIndex !== -1) {
      const primaryPage = nextDoc.files[fileIndex].pages[pageIndex];
      nextDoc.files[fileIndex].pages[pageIndex] = { ...primaryPage, ...sharedPageChanges };

      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.pageUpdate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        change: sharedPageChanges,
        createdOn: new Date().getTime()
      });
    }

    if (nextDoc.files[fileIndex].views && nextDoc.files[fileIndex].views.length > 0) {
      nextDoc.files[fileIndex].views = nextDoc.files[fileIndex].views.map((targetView, targetViewIndex) => {

        if (isEditingIndependentMobileView && targetViewIndex === mobileViewIndex) return targetView;

        const targetViewPageIndex = targetView.pages.findIndex((page) => pageId === page._id);
        if (targetViewPageIndex === -1) return targetView;

        changelogs.push({
          sdk: 'js',
          v: 1,
          view: targetView.type,
          target: ChangelogTypes.pageUpdate,
          _id: doc._id,
          identifier: doc.identifier,
          fileId,
          pageId,
          change: sharedPageChanges,
          createdOn: new Date().getTime()
        });

        const nextView = { ...targetView, pages: [...targetView.pages] };
        nextView.pages[targetViewPageIndex] = {
          ...nextView.pages[targetViewPageIndex],
          ...sharedPageChanges
        };
        return nextView;

      });
    }
  }

  if (onChange) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

export const handlePageDelete = (doc, view, fileId, pageId, onChange) => {

  const clonedDoc = JSON.parse(JSON.stringify(doc));
  const fileIndex = clonedDoc.files.findIndex((file) => file._id === fileId);
  const changelogs = [];

  /**
   * Step 1: Update primary view
   */
  const pageIndex = clonedDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);
  if (pageIndex !== -1) clonedDoc.files[fileIndex].pages.splice(pageIndex, 1);

  if (clonedDoc.files[fileIndex].pageOrder) {
    const pageOrderIndex = clonedDoc.files[fileIndex].pageOrder.findIndex((id) => id === pageId);
    if (pageOrderIndex !== -1) clonedDoc.files[fileIndex].pageOrder.splice(pageOrderIndex, 1);
  } else {
    clonedDoc.files[fileIndex].pageOrder = getPageOrder(null, clonedDoc.files[fileIndex].pages);
  }

  changelogs.push({
    sdk: 'js',
    v: 1,
    target: ChangelogTypes.pageDelete,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    pageId,
    createdOn: new Date().getTime()
  });

  /**
   * Step 2: Update alternative views
   */
  clonedDoc.files[fileIndex].views = clonedDoc.files[fileIndex]?.views?.length > 0 ?
    clonedDoc.files[fileIndex].views.map((view) => {

      changelogs.push({
        sdk: 'js',
        v: 1,
        view: view.type,
        target: ChangelogTypes.pageDelete,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        createdOn: new Date().getTime()
      });

      const nextView = { ...view };
      const viewPageIndex = view.pages.findIndex((page) => pageId === page._id);
      if (viewPageIndex !== -1) nextView.pages.splice(viewPageIndex, 1);

      if (nextView.pageOrder) {
        const pageOrderIndex = nextView.pageOrder.findIndex((id) => id === pageId);
        if (pageOrderIndex !== -1) nextView.pageOrder.splice(pageOrderIndex, 1);
      } else {
        nextView.pageOrder = getPageOrder(null, nextView.pages);
      }

      return nextView;

    }) : [];

  const { deletedFields, nextDoc } = removeOrphanedFieldsFromJoydoc(clonedDoc);

  if (deletedFields.length > 0) {

    deletedFields.forEach((field) => {

      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.fieldDelete,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        fieldId: field._id,
        createdOn: new Date().getTime()
      });

    });

  }

  if (onChange) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

export const handlePageMoveLeft = (doc, view, fileId, pageId, onChange) => {

  const clonedDoc = { ...doc, files: [...doc.files] };
  const fileIndex = clonedDoc.files.findIndex((file) => file._id === fileId);
  clonedDoc.files[fileIndex] = {
    ...clonedDoc.files[fileIndex],
    pages: [...clonedDoc.files[fileIndex].pages],
    pageOrder: [...(clonedDoc.files[fileIndex].pageOrder || [])],
  };
  const changelogs = [];

  /**
   * Step 1: Update Primary View
   */

  const pageIndex = clonedDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);

  if (pageIndex === 0 || pageIndex === -1) return clonedDoc; //left most page can't be moved

  const targetPageIndex = pageIndex - 1;

  const nextPages = [...clonedDoc.files[fileIndex].pages];

  nextPages[pageIndex] = nextPages.splice(targetPageIndex, 1, nextPages[pageIndex])[0];

  clonedDoc.files[fileIndex].pages = nextPages;

  clonedDoc.files[fileIndex].pageOrder = getPageOrder(null, nextPages);

  changelogs.push({
    sdk: 'js',
    v: 1,
    target: ChangelogTypes.pageMoveLeft,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    pageId,
    createdOn: new Date().getTime()
  });

  /**
   *  Step 2: Update Alternative Views
   */
  clonedDoc.files[fileIndex].views = clonedDoc.files[fileIndex]?.views?.length > 0 ?
    clonedDoc.files[fileIndex].views.map((view) => {

      const nextView = { ...view };
      const viewPageIndex = view.pages.findIndex((page) => pageId === page._id);

      if (viewPageIndex === 0 || viewPageIndex === -1) return nextView; //left most page can't be moved

      const targetViewPageIndex = viewPageIndex - 1;
      const nextViewPages = [...nextView.pages];

      nextViewPages[viewPageIndex] = nextViewPages.splice(targetViewPageIndex, 1, nextViewPages[viewPageIndex])[0];

      nextView.pages = nextViewPages;

      nextView.pageOrder = getPageOrder(null, nextViewPages);

      changelogs.push({
        sdk: 'js',
        v: 1,
        view: view.type,
        target: ChangelogTypes.pageMoveLeft,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        createdOn: new Date().getTime()
      });

      return nextView;

    }) : [];

  if (onChange) {
    onChange(
      changelogs,
      clonedDoc
    );
  }

  return clonedDoc;

};

export const handlePageMoveRight = (doc, view, fileId, pageId, onChange) => {

  const clonedDoc = { ...doc, files: [...doc.files] };
  const fileIndex = clonedDoc.files.findIndex((file) => file._id === fileId);
  clonedDoc.files[fileIndex] = {
    ...clonedDoc.files[fileIndex],
    pages: [...clonedDoc.files[fileIndex].pages],
    pageOrder: [...(clonedDoc.files[fileIndex].pageOrder || [])],
  };
  const changelogs = [];

  /**
   * Step 1: Update Primary View
   */

  const pageIndex = clonedDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);

  if (pageIndex === (clonedDoc.files[fileIndex].pages.length - 1) || pageIndex === -1) return clonedDoc; //Last page can't be moved towards the right

  const targetPageIndex = pageIndex + 1;

  const nextPages = [...clonedDoc.files[fileIndex].pages];

  nextPages[pageIndex] = nextPages.splice(targetPageIndex, 1, nextPages[pageIndex])[0];

  clonedDoc.files[fileIndex].pages = nextPages;

  clonedDoc.files[fileIndex].pageOrder = getPageOrder(null, nextPages);

  changelogs.push({
    sdk: 'js',
    v: 1,
    target: ChangelogTypes.pageMoveLeft,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    pageId,
    createdOn: new Date().getTime()
  });

  /**
   *  Step 2: Update Alternative Views
   */
  clonedDoc.files[fileIndex].views = clonedDoc.files[fileIndex]?.views?.length > 0 ?
    clonedDoc.files[fileIndex].views.map((view) => {

      const nextView = { ...view };
      const viewPageIndex = view.pages.findIndex((page) => pageId === page._id);

      if (viewPageIndex === (nextView.pages.length - 1) || viewPageIndex === -1) return nextView; //left most page can't be moved

      const targetViewPageIndex = viewPageIndex + 1;
      const nextViewPages = [...nextView.pages];

      nextViewPages[viewPageIndex] = nextViewPages.splice(targetViewPageIndex, 1, nextViewPages[viewPageIndex])[0];

      nextView.pages = nextViewPages;

      nextView.pageOrder = getPageOrder(null, nextViewPages);

      changelogs.push({
        sdk: 'js',
        v: 1,
        view: view.type,
        target: ChangelogTypes.pageMoveLeft,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        createdOn: new Date().getTime()
      });

      return nextView;

    }) : [];

  if (onChange) {
    onChange(
      changelogs,
      clonedDoc
    );
  }

  return clonedDoc;

};

export const handleFieldCreate = (doc, view, fileId, pageId, newFieldPayload, onChange, useReadableIds) => {

  const { field, fieldPosition } = getFieldAndFieldPositionFromCombinedPayloadForCreation(newFieldPayload);

  const changelogs = [];
  const nextDoc = { ...doc, fields: [...doc.fields], files: [...doc.files] };
  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], pages: [...nextDoc.files[fileIndex].pages] };

  const formattedFieldPosition = {
    ...fieldPosition,
    y: Math.round(fieldPosition.y),
    x: Math.round(fieldPosition.x),

    /**
     * Width and height should always be at least 1. For instance, for a number
     * like 0.4 we need to prevent the round down so we use Math.ceil(0.4) to
     * always round up so the valuse is 1 and not 0.
     */
    width: Math.ceil(fieldPosition.width),
    height: Math.ceil(fieldPosition.height),
  };

  /**
   * Step 1: Create field
   *
   * Why do we check if a field already exists?
   * A field can already exist if a user is creating multiple formattedFieldPositions for the same field.
   * For instance, a user is adding individual checkboxes for a mutliSelect field. In this case
   * we only want to add the formattedFieldPositions (Step 2) and skip adding a field.
   */
  // const existingFieldIndex = nextDoc.fields.findIndex((f) => f._id === field._id && field.file === fileId);
  const existingFieldIndex = nextDoc.fields.findIndex((f) => {
    const withinView = f._id === field._id && field.file === fileId;
    const duplicateOriginal = newFieldPayload.field === f._id;
    return withinView || duplicateOriginal;
  });

  if (existingFieldIndex === -1) {

    const newField = {
      file: fileId,
      ...field,
    };

    nextDoc.fields = [...nextDoc.fields, newField];

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      change: newField,
      createdOn: new Date().getTime()
    });

  }

  /**
   * Step 2: Create fieldPosition
   */

  /**
   * Step 2.1 Mobile View Target
   */
  if (view === FileViews.mobile) {

    /**
     * Mobile View only support original displayType
     */
    formattedFieldPosition.displayType = FieldDisplayTypes.original;

    /**
     * Enforce full width and left most positioning on mobile view.
     */
    formattedFieldPosition.width = 1;
    formattedFieldPosition.x = 0;

    /**
     * Mobile View Step 2.1.1: Generate (if needed) and add mobile view to the file if mobile view is active
     */
    nextDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(nextDoc.files[fileIndex]);

    const mobileViewIndex = nextDoc.files[fileIndex].views.findIndex((view) => view.type === FileViews.mobile);
    nextDoc.files[fileIndex].views = [...nextDoc.files[fileIndex].views];
    nextDoc.files[fileIndex].views[mobileViewIndex] = {
      ...nextDoc.files[fileIndex].views[mobileViewIndex],
      pages: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages]
    };
    const mobileViewPageIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages.findIndex((page) => pageId === page._id);
    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex] = {
      ...nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex],
      fieldPositions: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions]
    };

    /**
     * Mobile View Step 2.1.2: Add new field only to the mobile view
     */
    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions.push(formattedFieldPosition);

    changelogs.push({
      sdk: 'js',
      v: 1,
      view,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      change: formattedFieldPosition,
      createdOn: new Date().getTime()
    });

  }

  /**
   * Step 2.2: Header and Footer Target
   */
  else if (pageId === HeaderTarget || pageId === FooterTarget) {

    //Renamed for clarity
    const targetHeaderFooterKey = pageId;

    if (!nextDoc.files[fileIndex][targetHeaderFooterKey]) return console.warn(`${pageId} does not exist in the file`);

    nextDoc.files[fileIndex][targetHeaderFooterKey] = {
      ...nextDoc.files[fileIndex][targetHeaderFooterKey],
      fieldPositions: [...(nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions || [])]
    };

    //Add fieldPosition to the header or footer
    nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions.push(formattedFieldPosition);

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      change: formattedFieldPosition,
      createdOn: new Date().getTime()
    });

  }

  /**
   * Step 2.3: Primary View Target
   */
  else {

    const pageIndex = nextDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);
    if (pageIndex === -1) return console.warn(`${pageId} does not exist in the file`);

    nextDoc.files[fileIndex].pages[pageIndex] = {
      ...nextDoc.files[fileIndex].pages[pageIndex],
      fieldPositions: [...nextDoc.files[fileIndex].pages[pageIndex].fieldPositions]
    };
    nextDoc.files[fileIndex].pages[pageIndex].fieldPositions.push(formattedFieldPosition);

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      change: formattedFieldPosition,
      createdOn: new Date().getTime()
    });

  }

  if (onChange) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

export const handleFieldDuplicate = (doc, view, fileId, pageId, fieldId, fieldPositionId, onChange, useReadableIds) => {
  const nextDoc = { ...doc, fields: [...doc.fields], files: [...doc.files] };
  const changelogs = [];

  /**
   * Step 1: Duplicate the field
   */
  const originalFieldIndex = nextDoc.fields.findIndex((f) => f._id === fieldId);
  if (originalFieldIndex === -1) return console.warn('Field not found');

  const originalField = nextDoc.fields[originalFieldIndex];

  // Find fileIndex for context
  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], pages: [...nextDoc.files[fileIndex].pages] };

  // Duplicate the field
  const newField = {
    ...originalField,
    _id: generateId(useReadableIds, {
      fields: nextDoc.fields,
      pages: nextDoc.files[fileIndex]?.pages,
      views: nextDoc.files[fileIndex]?.views
    }, originalField.type),
  };

  // Remove formulas from the duplicated field
  delete newField.formulas;
  nextDoc.fields.push(newField);

  changelogs.push({
    sdk: 'js',
    v: 1,
    target: ChangelogTypes.fieldCreate,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    change: newField,
    createdOn: new Date().getTime()
  });

  /**
   * Step 2: Duplicate the field position
   */

  const file = nextDoc.files[fileIndex];
  if (!file) return console.warn('File not found');

  /**
   * Step 2.1: Header or footer field position duplication
   */
  if (pageId === HeaderTarget || pageId === FooterTarget) {

    const targetHeaderFooterKey = pageId;

    if (!file[targetHeaderFooterKey]) return console.warn(`${page} doest not exist in the file`);

    const fieldPosition = file[targetHeaderFooterKey]?.fieldPositions.find((fp) => fp._id === fieldPositionId);
    if (!fieldPosition) return console.warn('FieldPosition not found');

    const newFieldPosition = {
      ...fieldPosition,
      _id: generateObjectId(),
      field: newField._id,
      y: Math.round(fieldPosition.y + fieldPosition.height)
    };

    nextDoc.files[fileIndex][targetHeaderFooterKey] = {
      ...file[targetHeaderFooterKey],
      fieldPositions: [...(file[targetHeaderFooterKey].fieldPositions || []), newFieldPosition]
    };

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      change: newFieldPosition,
      createdOn: new Date().getTime()
    });
  }

  else if (view === FileViews.mobile) {

    // Handle mobile view
    nextDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(nextDoc.files[fileIndex]);
    nextDoc.files[fileIndex].views = [...nextDoc.files[fileIndex].views];
    const mobileViewIndex = nextDoc.files[fileIndex].views.findIndex((v) => v.type === 'mobile');
    nextDoc.files[fileIndex].views[mobileViewIndex] = {
      ...nextDoc.files[fileIndex].views[mobileViewIndex],
      pages: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages]
    };

    const mobileViewPageIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages.findIndex((p) => p._id === pageId);
    const mobilePage = nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex];

    const fieldPosition = mobilePage.fieldPositions.find((fp) => fp._id === fieldPositionId);

    const newMobileFieldPosition = {
      ...fieldPosition,
      _id: generateObjectId(),
      field: newField._id,
      y: Math.round(fieldPosition.y + fieldPosition.height),
    };

    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex] = {
      ...mobilePage,
      fieldPositions: [...mobilePage.fieldPositions, newMobileFieldPosition]
    };

    changelogs.push({
      sdk: 'js',
      v: 1,
      view,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      change: newMobileFieldPosition,
      createdOn: new Date().getTime()
    });
  } else {

    // Handle primary view
    const pageIndex = file.pages.findIndex((p) => p._id === pageId);
    if (pageIndex === -1) return console.warn('Page not found');

    const page = file.pages[pageIndex];
    const fieldPosition = page.fieldPositions.find((fp) => fp._id === fieldPositionId);
    if (!fieldPosition) return console.warn('FieldPosition not found');

    const newFieldPosition = {
      ...fieldPosition,
      _id: generateObjectId(),
      field: newField._id,
      y: Math.round(fieldPosition.y + fieldPosition.height)
    };

    nextDoc.files[fileIndex].pages[pageIndex] = {
      ...page,
      fieldPositions: [...page.fieldPositions, newFieldPosition]
    };

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      change: newFieldPosition,
      createdOn: new Date().getTime()
    });
  }

  if (onChange) {
    onChange(changelogs, nextDoc);
  }

  return nextDoc;

};

export const handleDuplicateFieldPositionOnly = (doc, view, fileId, pageId, fieldId, fieldPositionId, onChange) => {

  const nextDoc = { ...doc, files: [...doc.files] };
  const changelogs = [];
  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], pages: [...nextDoc.files[fileIndex].pages] };

  if (view === FileViews.mobile) {
    nextDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(nextDoc.files[fileIndex]);
    nextDoc.files[fileIndex].views = [...nextDoc.files[fileIndex].views];
    const mobileViewIndex = nextDoc.files[fileIndex].views.findIndex((v) => v.type === 'mobile');
    nextDoc.files[fileIndex].views[mobileViewIndex] = {
      ...nextDoc.files[fileIndex].views[mobileViewIndex],
      pages: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages]
    };

    const mobileViewPageIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages.findIndex((p) => p._id === pageId);
    const mobilePage = nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex];

    const fieldPosition = mobilePage.fieldPositions.find((fp) => fp._id === fieldPositionId);

    const newFieldPosition = {
      ...fieldPosition,
      _id: generateObjectId(),
      y: fieldPosition.y + fieldPosition.height,
    };


    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex] = {
      ...mobilePage,
      fieldPositions: [...mobilePage.fieldPositions, newFieldPosition]
    };

    // Log the change
    changelogs.push({
      sdk: 'js',
      v: 1,
      view,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      change: newFieldPosition,
      createdOn: new Date().getTime()
    });
  } else {
    // Primary View
    const file = nextDoc.files[fileIndex];
    if (!file) {
      console.warn('File not found');
      return;
    }

    // Find the page
    const pageIndex = file.pages.findIndex((p) => p._id === pageId);
    if (pageIndex === -1) {
      console.warn('Page not found');
      return;
    }

    const page = file.pages[pageIndex];

    //  find the fieldPosition
    const fieldPosition = page.fieldPositions.find((fp) => fp._id === fieldPositionId);

    // Duplicate the fieldPosition
    const newFieldPosition = {
      ...fieldPosition,
      _id: generateObjectId(),
      y: fieldPosition.y + fieldPosition.height,
    };


    nextDoc.files[fileIndex].pages[pageIndex] = {
      ...page,
      fieldPositions: [...page.fieldPositions, newFieldPosition]
    };

    // Log the change
    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldPositionCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      change: newFieldPosition,
      createdOn: new Date().getTime()
    });
  }

  // Trigger onChange callback if provided
  if (onChange) {
    onChange(changelogs, nextDoc);
  }

  return nextDoc;
};

export const handleFieldPositionLayoutsChange = (doc, view, fileId, pageId, layouts, onChange, measuringInProgress) => {

  const changelogs = [];
  const nextDoc = { ...doc, files: [...doc.files] };
  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], pages: [...nextDoc.files[fileIndex].pages] };
  const mobileViewIndex = nextDoc.files[fileIndex].views ? nextDoc.files[fileIndex].views.findIndex((view) => view.type === FileViews.mobile) : -1;

  //Prevent field layout updates for mobile view if it doesn't exist.
  if (view === FileViews.mobile && mobileViewIndex === -1) return nextDoc;

  /**
   * Mobile View Target
   */
  if (view === FileViews.mobile) {

    /**
     * Mobile View Step 1: Generate (if needed) and add mobile view to the file if mobile view is active
     */
    nextDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(nextDoc.files[fileIndex]);

    /**
     * Mobile View Step 2: Update the layouts of only the target view
     */
    nextDoc.files[fileIndex].views = [...nextDoc.files[fileIndex].views];
    nextDoc.files[fileIndex].views[mobileViewIndex] = { ...nextDoc.files[fileIndex].views[mobileViewIndex], pages: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages] };
    const mobileViewPageIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages.findIndex((page) => pageId === page._id);
    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex] = { ...nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex], fieldPositions: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions] };

    const layoutLookup = {};
    layouts.forEach((layout) => layoutLookup[layout._id] = layout);

    const nextFieldPositions = [];
    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions.forEach((fieldPosition) => {
      nextFieldPositions.push({
        ...fieldPosition,
        ...layoutLookup[fieldPosition._id]
      });

      changelogs.push({
        sdk: 'js',
        v: 1,
        view,
        target: ChangelogTypes.fieldPositionUpdate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        fieldPositionId: fieldPosition._id,
        change: layoutLookup[fieldPosition._id],
        createdOn: new Date().getTime()
      });

    });

    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions = nextFieldPositions;
  }
  /**
   * Header and Footer Target
   */
  else if (pageId === HeaderTarget || pageId === FooterTarget) {

    //Renamed for clarity
    const targetHeaderFooterKey = pageId;

    if (!nextDoc.files[fileIndex][targetHeaderFooterKey]) return console.warn(`${pageId} does not exist in the file`);
    nextDoc.files[fileIndex][targetHeaderFooterKey] = { ...nextDoc.files[fileIndex][targetHeaderFooterKey] };

    const layoutLookup = {};
    layouts.forEach((layout) => layoutLookup[layout._id] = layout);

    const nextFieldPositions = [];
    nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions.forEach((fieldPosition) => {

      nextFieldPositions.push({
        ...fieldPosition,
        ...layoutLookup[fieldPosition._id]
      });

      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.fieldPositionUpdate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        fieldPositionId: fieldPosition._id,
        change: layoutLookup[fieldPosition._id],
        createdOn: new Date().getTime()
      });

    });

    nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions = nextFieldPositions;

  }

  /**
   * Primary View Target
   */
  else {

    const pageIndex = nextDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);
    nextDoc.files[fileIndex].pages[pageIndex] = { ...nextDoc.files[fileIndex].pages[pageIndex] };

    const layoutLookup = {};
    layouts.forEach((layout) => layoutLookup[layout._id] = layout);

    const nextFieldPositions = [];
    nextDoc.files[fileIndex].pages[pageIndex].fieldPositions.forEach((fieldPosition) => {

      nextFieldPositions.push({
        ...fieldPosition,
        ...layoutLookup[fieldPosition._id]
      });

      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.fieldPositionUpdate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        fieldPositionId: fieldPosition._id,
        change: layoutLookup[fieldPosition._id],
        createdOn: new Date().getTime()
      });

    });

    nextDoc.files[fileIndex].pages[pageIndex].fieldPositions = nextFieldPositions;

  }

  if (onChange && !measuringInProgress) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

const handleFieldPositionChange = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, fieldPositionChanges, onChange) => {

  const nextDoc = { ...doc, files: [...doc.files] };

  const changelogs = [];

  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], pages: [...nextDoc.files[fileIndex].pages] };
  const pageIndex = nextDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);

  changelogs.push({
    sdk: 'js',
    v: 1,
    target: ChangelogTypes.fieldPositionUpdate,
    view,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    pageId,
    fieldId,
    fieldIdentifier,
    fieldPositionId,
    change: fieldPositionChanges,
    createdOn: new Date().getTime()
  });

  /**
   * Mobile View Target
   */
  if (view === FileViews.mobile) {

    /**
     * Mobile View Step 1: Generate (if needed) and add mobile view to the file if mobile view is active
     */
    nextDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(nextDoc.files[fileIndex]);

    /**
     * Mobile View Step 2: Update the layouts of only the target view
     */
    const mobileViewIndex = nextDoc.files[fileIndex].views.findIndex((view) => view.type === FileViews.mobile);
    nextDoc.files[fileIndex].views = [...nextDoc.files[fileIndex].views];
    nextDoc.files[fileIndex].views[mobileViewIndex] = { ...nextDoc.files[fileIndex].views[mobileViewIndex], pages: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages] };
    const mobileViewPageIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages.findIndex((page) => pageId === page._id);
    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex] = { ...nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex], fieldPositions: [...nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions] };

    const mobileViewTargetFieldPositionIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions
      .findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);

    const mobileViewTargetFieldPosition = nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions[mobileViewTargetFieldPositionIndex];

    /**
     * Step 3: Merging existing fieldPostion tableColumns with new changes
     */
    let mergedFieldPositionChanges = fieldPositionChanges;
    if (mobileViewTargetFieldPosition.tableColumns && fieldPositionChanges.tableColumns) {
      mergedFieldPositionChanges = {
        ...fieldPositionChanges,
        tableColumns: { ...mobileViewTargetFieldPosition.tableColumns, ...fieldPositionChanges.tableColumns }
      };
    }

    nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions[mobileViewTargetFieldPositionIndex] = {
      ...mobileViewTargetFieldPosition,
      ...mergedFieldPositionChanges
    };

  }

  /**
   * Header and Footer Target
   */
  else if (pageId === HeaderTarget || pageId === FooterTarget) {

    //Renamed for clarity
    const targetHeaderFooterKey = pageId;
    if (!nextDoc.files[fileIndex][targetHeaderFooterKey]) return console.warn(`${pageId} does not exist in the file`);
    nextDoc.files[fileIndex][targetHeaderFooterKey] = { ...nextDoc.files[fileIndex][targetHeaderFooterKey], fieldPositions: [...nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions] };

    /**
     * Header Footer Step 1: Update target fieldPosition
     */
    const targetFieldPositionIndex = nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions.findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);
    const targetFieldPosition = nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions[targetFieldPositionIndex];

    /**
     * Header Footer Step 2: Merging existing fieldPostion tableColumns with new changes
     */
    let mergedFieldPositionChanges = fieldPositionChanges;
    if (targetFieldPosition.tableColumns && fieldPositionChanges.tableColumns) {
      mergedFieldPositionChanges = {
        ...fieldPositionChanges,
        tableColumns: { ...targetFieldPosition.tableColumns, ...fieldPositionChanges.tableColumns }
      };
    }

    nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions[targetFieldPositionIndex] = {
      ...targetFieldPosition,
      ...mergedFieldPositionChanges,
    };

  }

  /**
   * Primary View Target
   */
  else {

    nextDoc.files[fileIndex].pages[pageIndex] = { ...nextDoc.files[fileIndex].pages[pageIndex], fieldPositions: [...nextDoc.files[fileIndex].pages[pageIndex].fieldPositions] };

    /**
     * Primary View Step 1: Update target fieldPosition
     */
    const targetFieldPositionIndex = nextDoc.files[fileIndex].pages[pageIndex].fieldPositions.findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);
    const targetFieldPosition = nextDoc.files[fileIndex].pages[pageIndex].fieldPositions[targetFieldPositionIndex];

    /**
     * PrimaryView Step 2: Merging existing fieldPostion tableColumns with new changes
     */
    let mergedFieldPositionChanges = fieldPositionChanges;
    if (targetFieldPosition.tableColumns && fieldPositionChanges.tableColumns) {
      mergedFieldPositionChanges = {
        ...fieldPositionChanges,
        tableColumns: { ...targetFieldPosition.tableColumns, ...fieldPositionChanges.tableColumns }
      };
    }

    nextDoc.files[fileIndex].pages[pageIndex].fieldPositions[targetFieldPositionIndex] = {
      ...targetFieldPosition,
      ...mergedFieldPositionChanges,
    };

  }

  if (onChange) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

const handleFieldChange = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, changes, collectionFieldParams, eventType, onChange, localTimezone) => {

  let nextDoc = { ...doc, fields: [...doc.fields] };
  const { fieldChanges, fieldPositionChanges } = separateFieldAndFieldPositionChangesForUpdate(changes);

  if (fieldPositionChanges || (collectionFieldParams && collectionFieldParams.oldColumnId)) {
    const _fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
    if (_fileIndex !== -1) {
      nextDoc.files = [...nextDoc.files];
      nextDoc.files[_fileIndex] = {
        ...nextDoc.files[_fileIndex],
        pages: nextDoc.files[_fileIndex].pages.map((p) => ({ ...p, fieldPositions: [...p.fieldPositions] })),
        views: nextDoc.files[_fileIndex].views
          ? nextDoc.files[_fileIndex].views.map((v) => ({
              ...v,
              pages: v.pages ? v.pages.map((p) => ({ ...p, fieldPositions: [...p.fieldPositions] })) : v.pages,
            }))
          : nextDoc.files[_fileIndex].views,
        header: nextDoc.files[_fileIndex].header
          ? { ...nextDoc.files[_fileIndex].header, fieldPositions: nextDoc.files[_fileIndex].header.fieldPositions ? [...nextDoc.files[_fileIndex].header.fieldPositions] : undefined }
          : nextDoc.files[_fileIndex].header,
        footer: nextDoc.files[_fileIndex].footer
          ? { ...nextDoc.files[_fileIndex].footer, fieldPositions: nextDoc.files[_fileIndex].footer.fieldPositions ? [...nextDoc.files[_fileIndex].footer.fieldPositions] : undefined }
          : nextDoc.files[_fileIndex].footer,
      };
    }
  }

  const changelogs = [];

  /**
   * Step 1: Handle field update changes
   */
  if (fieldChanges) {

    const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
    const field = nextDoc.fields[fieldIndex];

    if (localTimezone && !field.tz) fieldChanges.tz = localTimezone;

    nextDoc.fields[fieldIndex] = { ...nextDoc.fields[fieldIndex], ...fieldChanges };

    const isCollectionField = field.type === FieldTypes.collection;

    const changePayload = isCollectionField && collectionFieldParams
      ? collectionFieldParams
      : fieldChanges;

    /**
     * Step 1.1: Handle conditional logic updates when field ID changes
     */
    if (fieldChanges._id && fieldChanges._id !== fieldId) {
      // Update all conditional logic references from old ID to new ID
      nextDoc.fields = updateConditionalLogicFieldReferences(nextDoc.fields, fieldId, fieldChanges._id);
    }

    changelogs.push(handleFieldChangelogs({
      eventType: isCollectionField && eventType ? eventType : ChangelogTypes.fieldUpdate,
      doc,
      fileId,
      pageId,
      fieldPositionId,
      fieldId,
      fieldIdentifier,
      fieldChanges: changePayload,
    }));

    /**
     * Step 1.2: Handle table column ID changes for fieldPositions in all views
     */
    if (
      collectionFieldParams
      && collectionFieldParams.oldColumnId
      && collectionFieldParams.newColumnId
      && collectionFieldParams.oldColumnId !== collectionFieldParams.newColumnId
    ) {
      if (field.type === FieldTypes.table) nextDoc = syncTableFieldColumnIdChangeInAllViewFieldPositions(nextDoc, fileId, fieldId, collectionFieldParams);
      if (field.type === FieldTypes.collection) nextDoc = syncCollectionFieldColumnIdChange(nextDoc, fileId, fieldId, fieldIndex, collectionFieldParams);
    }

  }

  /**
   * Step 2: Handle fieldPosition update changes
   */
  if (fieldPositionChanges) {

    const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
    const pageIndex = nextDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldPositionUpdate,
      view,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldPositionId,
      fieldId,
      fieldIdentifier,
      change: fieldPositionChanges,
      createdOn: new Date().getTime()
    });

    /**
     * Mobile View Target
     * 
     * If the user modifies the readableId and mobile view is active, update the fieldPositions in mobileView
     * 
     */
    if (view === FileViews.mobile) {
      /**
       * Mobile View Step 1: Generate (if needed) and add mobile view to the file if mobile view is active
       */
      nextDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(nextDoc.files[fileIndex]);

      /**
       * Mobile View Step 2: Update the layouts of only the target view
       */
      const mobileViewIndex = nextDoc.files[fileIndex].views.findIndex((view) => view.type === FileViews.mobile);

      const mobileViewPageIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages.findIndex((page) => pageId === page._id);

      const mobileViewTargetFieldPositionIndex = nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions
        .findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);


      if (mobileViewTargetFieldPositionIndex !== -1) {

        nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions[mobileViewTargetFieldPositionIndex] = {
          ...nextDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions[mobileViewTargetFieldPositionIndex],
          ...fieldPositionChanges
        };

      }


    }
    /**
    * Header / Footer Target
    */
    else if (pageId === HeaderTarget || pageId === FooterTarget) {

      const targetHeaderFooterKey = pageId;
      if (!nextDoc.files[fileIndex][targetHeaderFooterKey]) return console.warn(`${pageId} does not exist in the file`);

      const targetFieldPositionIndex = nextDoc.files[fileIndex][targetHeaderFooterKey]?.fieldPositions.findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);

      nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions[targetFieldPositionIndex] = {
        ...nextDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions[targetFieldPositionIndex],
        ...fieldPositionChanges,
      };

    }
    /**
     * Primary View Target
     */
    else {

      const targetFieldPositionIndex = nextDoc.files[fileIndex].pages[pageIndex].fieldPositions.findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);

      if (targetFieldPositionIndex === -1) return console.warn(`Field position does not exist in the page`);

      nextDoc.files[fileIndex].pages[pageIndex].fieldPositions[targetFieldPositionIndex] = {
        ...nextDoc.files[fileIndex].pages[pageIndex].fieldPositions[targetFieldPositionIndex],
        ...fieldPositionChanges,
      };

    }

    /**
     * TODO: Fix this logic Logic below is to update the readableId of all other fieldPositions that are associated with the same field
     * 
     * This is currently commented out because it is not working as expected. This needs to be revisited. This needs to be created as a 
     * separate function that can be called and check the primary view, mobile view, header, and footer fieldPositions to ensure that all
     * fieldPositions associated with the same field are updated properly.
     */
    if (fieldPositionChanges && fieldPositionChanges.field) {
      nextDoc = updateAllFieldPositionAssociatedWithField(nextDoc, fileId, fieldId, { field: fieldPositionChanges.field });
    }
  }

  /**
   * Step 3: Resolve formulas after field changes
   * This will evaluate all formulas and update any dependent fields
   */
  if (fieldChanges) {
    const formulaResolution = resolveFormulasAndGenerateChangelogs(nextDoc, fieldId, fileId, pageId);
    // Apply any field updates from formula resolution
    nextDoc.fields = formulaResolution.doc.fields;
    // Add formula-driven changelogs
    changelogs.push(...formulaResolution.changelogs);
  }

  if (onChange) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

const handleFieldChangelogs = ({
  eventType,
  doc,
  fileId,
  pageId,
  fieldPositionId,
  fieldId,
  fieldIdentifier,
  fieldChanges,
}) => ({
  sdk: 'js',
  v: 1,
  target: eventType,
  _id: doc._id,
  identifier: doc.identifier,
  fileId,
  pageId,
  fieldPositionId,
  fieldId,
  fieldIdentifier,
  change: fieldChanges,
  createdOn: new Date().getTime(),
});

const buildTableFieldRowCellChangeResult = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change, timezone) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
  const field = nextDoc.fields[fieldIndex];

  /**
   * Step 1: Handle doc update
   */
  const nextRows = [...field.value];
  const rowIndex = nextRows.findIndex((row) => row._id === rowId);
  const targetRow = nextRows[rowIndex];

  nextRows[rowIndex] = {
    ...targetRow,
    ...(timezone && !targetRow.tz ? { tz: timezone } : {}),
    cells: {
      ...targetRow.cells,
      [columnId]: change
    }
  };

  nextDoc.fields[fieldIndex] = { ...field, value: nextRows };

  const changeObject = {
    rowId,
    row: { _id: rowId, cells: { [columnId]: change } }
  };

  if (timezone) {
    changeObject.row.tz = timezone;
  }

  const changelogs = [{
    sdk: 'js',
    v: 1,
    target: ChangelogTypes.fieldRowUpdate,
    _id: doc._id,
    identifier: doc.identifier,
    fileId,
    pageId,
    fieldPositionId,
    fieldId,
    fieldIdentifier,
    change: changeObject,
    createdOn: new Date().getTime()
  }];

  /**
   * Step 2: Resolve formulas after table field changes
   * This will evaluate all formulas and update any dependent fields
   */
  const formulaResolution = resolveFormulasAndGenerateChangelogs(nextDoc, fieldId, fileId, pageId);
  // Apply any field updates from formula resolution
  nextDoc.fields = formulaResolution.doc.fields;
  // Add formula-driven changelogs
  changelogs.push(...formulaResolution.changelogs);

  return { nextDoc, changelogs };

};

const buildTableFieldRowCellBatchChangeResult = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, cellChanges, timezone, signer) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
  const field = nextDoc.fields[fieldIndex];

  const nextRows = [...field.value];
  const rowIndex = nextRows.findIndex((row) => row._id === rowId);
  const targetRow = nextRows[rowIndex];

  const mergedCells = { ...targetRow.cells };
  const columnIds = Object.keys(cellChanges);

  for (let i = 0; i < columnIds.length; i++) {
    mergedCells[columnIds[i]] = cellChanges[columnIds[i]];
  }

  nextRows[rowIndex] = {
    ...targetRow,
    ...(timezone && !targetRow.tz ? { tz: timezone } : {}),
    ...(signer ? { signer } : {}),
    cells: mergedCells
  };
  nextDoc.fields[fieldIndex] = { ...field, value: nextRows };

  const changelogs = [];
  const createdOn = new Date().getTime();

  for (let i = 0; i < columnIds.length; i++) {
    const columnId = columnIds[i];
    const changeObject = {
      rowId,
      row: { _id: rowId, cells: { [columnId]: cellChanges[columnId] } }
    };
    if (timezone) changeObject.row.tz = timezone;
    if (signer) changeObject.row.signer = signer;

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldRowUpdate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldPositionId,
      fieldId,
      fieldIdentifier,
      change: changeObject,
      createdOn
    });
  }

  const formulaResolution = resolveFormulasAndGenerateChangelogs(nextDoc, fieldId, fileId, pageId);
  nextDoc.fields = formulaResolution.doc.fields;
  changelogs.push(...formulaResolution.changelogs);

  return { nextDoc, changelogs };

};

const handleTableFieldRowCellChange = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change, onChange, timezone) => {

  const { nextDoc, changelogs } = buildTableFieldRowCellChangeResult(
    doc,
    view,
    fileId,
    pageId,
    fieldId,
    fieldIdentifier,
    fieldPositionId,
    rowId,
    columnId,
    change,
    timezone
  );

  if (onChange) {
    onChange(changelogs, nextDoc);
  }

  return nextDoc;

};

const handleTableFieldRowAddBelow = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
  const field = nextDoc.fields[fieldIndex];

  handleInsertTableRow(rowIndex, ({ targetRowIndex, row }) => {

    /**
     * Step 1: Handle document update
     */
    const nextRows = [...field.value];
    const nextRowOrder = [...getRowOrder(field.rowOrder, nextRows)];

    nextRowOrder.splice(targetRowIndex, 0, row._id);

    //Logic to add default value to newly created row
    field.tableColumns.forEach((column) => {

      if (column.deleted) return;

      row.cells[column._id] = column.value || '';

    });

    nextRows.push(row);

    nextDoc.fields[fieldIndex] = { ...field, value: nextRows, rowOrder: nextRowOrder };

    const cleanedRow = cleanRow(row);

    /**
     * Step 2: Fire onChange event
     */
    if (onChange) {
      onChange(
        [
          {
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fieldRowCreate,
            _id: doc._id,
            identifier: doc.identifier,
            fileId,
            pageId,
            fieldPositionId,
            fieldId,
            fieldIdentifier,
            change: { targetRowIndex, row: cleanedRow },
            createdOn: new Date().getTime()
          }
        ],
        nextDoc
      );
    }

  });

  return nextDoc;

};

const handleTableFieldRowDuplicate = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex, onChange) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
  const field = nextDoc.fields[fieldIndex];

  handleDuplicateTableRow(field.value, rowId, rowIndex, ({ targetRowIndex, row }) => {

    /**
     * Step 1: Handle document update
     */
    const nextRows = [...field.value];
    const nextRowOrder = [...getRowOrder(field.rowOrder, nextRows)];

    nextRowOrder.splice(targetRowIndex, 0, row._id);
    nextRows.push(row);

    nextDoc.fields[fieldIndex] = { ...field, value: nextRows, rowOrder: nextRowOrder };

    /**
     * Step 2: Fire onChange event
     */
    if (onChange) {
      onChange(
        [
          {
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fieldRowCreate,
            _id: doc._id,
            identifier: doc.identifier,
            fileId,
            pageId,
            fieldPositionId,
            fieldId,
            fieldIdentifier,
            change: { targetRowIndex, row },
            createdOn: new Date().getTime()
          }
        ],
        nextDoc
      );
    }

  });

  return nextDoc;

};

const handleTableFieldRowMoveUp = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
  const field = nextDoc.fields[fieldIndex];

  handleMoveTableRowUp(rowIndex, ({ targetRowIndex }) => {

    /**
     * Step 1: Handle document update
     */
    const nextRowOrder = [...getRowOrder(field.rowOrder, field.value)];
    const rowId = nextRowOrder[rowIndex];
    if (rowIndex !== -1) nextRowOrder[rowIndex] = nextRowOrder.splice(targetRowIndex, 1, nextRowOrder[rowIndex])[0];

    nextDoc.fields[fieldIndex] = { ...field, rowOrder: nextRowOrder };

    /**
     * Step 2: Fire onChange event
     */
    if (onChange) {
      onChange(
        [
          {
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fieldRowMove,
            _id: doc._id,
            identifier: doc.identifier,
            fileId,
            pageId,
            fieldPositionId,
            fieldId,
            fieldIdentifier,
            change: { rowId, targetRowIndex },
            createdOn: new Date().getTime()
          }
        ],
        nextDoc
      );
    }

  });

  return nextDoc;

};

const handleTableFieldRowMoveDown = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
  const field = nextDoc.fields[fieldIndex];

  const nextRowOrder = [...getRowOrder(field.rowOrder, field.value)];

  handleMoveTableRowDown(nextRowOrder, rowIndex, ({ targetRowIndex }) => {

    /**
     * Step 1: Handle document update
     */
    const rowId = nextRowOrder[rowIndex];
    if (rowIndex !== -1) nextRowOrder[rowIndex] = nextRowOrder.splice(targetRowIndex, 1, nextRowOrder[rowIndex])[0];

    nextDoc.fields[fieldIndex] = { ...field, rowOrder: nextRowOrder };

    /**
     * Step 2: Fire onChange event
     */
    if (onChange) {
      onChange(
        [
          {
            sdk: 'js',
            v: 1,
            target: ChangelogTypes.fieldRowMove,
            _id: doc._id,
            identifier: doc.identifier,
            fileId,
            pageId,
            fieldPositionId,
            fieldId,
            fieldIdentifier,
            change: { rowId, targetRowIndex },
            createdOn: new Date().getTime()
          }
        ],
        nextDoc
      );
    }

  });

  return nextDoc;

};

const handleTableFieldRowDelete = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex, onChange) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);
  const field = nextDoc.fields[fieldIndex];

  /**
   * Step 1: Handle doc update
   *
   * What's the difference between rowIndex and targetRowIndex?
   * * rowIndex points at the index of the rowId inside field.rowOrder
   * * valueRowObjectIndex points at the index of the rowId inside field.value
   */
  const nextRowOrder = [...getRowOrder(field.rowOrder, field.value)];

  const rowId = nextRowOrder[rowIndex];
  nextRowOrder.splice(rowIndex, 1);

  const nextRows = [...field.value];
  const valueRowObjectIndex = nextRows.findIndex((row) => row._id === rowId);
  nextRows[valueRowObjectIndex] = { ...nextRows[valueRowObjectIndex], deleted: true };

  nextDoc.fields[fieldIndex] = { ...field, value: nextRows, rowOrder: nextRowOrder };

  /**
   * Step 2: Fire onChange event
   */
  if (onChange) {
    onChange(
      [
        {
          sdk: 'js',
          v: 1,
          target: ChangelogTypes.fieldRowDelete,
          _id: doc._id,
          identifier: doc.identifier,
          fileId,
          pageId,
          fieldPositionId,
          fieldId,
          fieldIdentifier,
          change: { rowId },
          createdOn: new Date().getTime()
        }
      ],
      nextDoc
    );
  }

  return nextDoc;

};

const handleTableFieldColumnDelete = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, columnId, onChange) => {

  const nextDoc = { ...doc, fields: [...doc.fields], files: [...doc.files] };

  const changelogs = [];

  /**
   * Field Update Change log
   *
   * 1. Identify the targetFieldIndex using fileId, fieldId and fieldIdentifier
   * 2. If field present at targetFieldIndex has tableColumns
   *   - Identify index of the deleted column using columnId
   *   - Remove the deleted column from tableColumns and tableColumnOrder
   *   - Pass the updated coulmn and columnOrder as the changes to the changelog
   */

  if (nextDoc && nextDoc.fields.length > 0) {

    const fieldChanges = {};

    const targetFieldIndex = nextDoc.fields.findIndex((item) => (item.file === fileId && item._id === fieldId && item.identifier === fieldIdentifier));

    const targetField = { ...nextDoc.fields[targetFieldIndex] };

    if (targetField && targetField.tableColumns && targetField.tableColumns.length > 0) {

      const nextTableColumns = [...targetField.tableColumns];
      const nextTableColumnOrder = getColumnOrder(targetField.tableColumnOrder, nextTableColumns);

      const targetColumnIndex = nextTableColumns.findIndex((item) => item._id === columnId);
      const targetColumnOrderIndex = nextTableColumnOrder.findIndex((item) => item === columnId);

      if (targetColumnIndex !== -1) {
        nextTableColumns.splice(targetColumnIndex, 1);
        targetField.tableColumns = nextTableColumns;
        fieldChanges.tableColumns = nextTableColumns;
      }

      if (targetColumnOrderIndex !== -1) {
        nextTableColumnOrder.splice(targetColumnOrderIndex, 1);
        targetField.tableColumnOrder = nextTableColumnOrder;
        fieldChanges.tableColumnOrder = nextTableColumnOrder;
      }

      nextDoc.fields[targetFieldIndex] = targetField;

    }

    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.fieldUpdate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      columnId,
      change: fieldChanges,
      createdOn: new Date().getTime()
    });

  }

  /**
   * FieldPosition deletion changelog
   *
   * Primary view:
   *  - Identify the targetFile and find out the pageIndex of the pageId that is passed via props
   *  - Find targetFieldPositionIndex by using the fieldId and columnId
   *    (item.field === fieldPosition.field // fieldId check
   *      item.column === fieldPosition.column // columnId check)
   *  - Splice the element present at targetFieldPositionIndex and pass the updated fieldPosition to changelog
   *
   * Mobile View
   *  - We won't perform any updates on mobile since we don't have designated fieldPositions associated with
   *    individual table cells on mobile
  */

  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);
  nextDoc.files[fileIndex] = { ...nextDoc.files[fileIndex], pages: [...nextDoc.files[fileIndex].pages] };

  const pageIndex = nextDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);
  nextDoc.files[fileIndex].pages[pageIndex] = { ...nextDoc.files[fileIndex].pages[pageIndex] };

  const nextFieldPositions = [];

  nextDoc.files[fileIndex].pages[pageIndex].fieldPositions.forEach((fieldPosition) => {

    if (fieldPosition.field === fieldId && fieldPosition.column && fieldPosition.column === columnId) {
      changelogs.push({
        sdk: 'js',
        v: 1,
        view,
        target: ChangelogTypes.fieldPositionDelete,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        pageId,
        fieldId,
        fieldIdentifier,
        fieldPositionId: fieldPosition._id,
        columnId,
        createdOn: new Date().getTime()
      });

    }
    else {
      nextFieldPositions.push(fieldPosition);
    }

  });

  nextDoc.files[fileIndex].pages[pageIndex].fieldPositions = nextFieldPositions;

  if (onChange) {
    onChange(
      changelogs,
      nextDoc
    );
  }

  return nextDoc;

};

const handleFieldFileUpload = (doc, view, fileId, pageId, fieldId, fieldIdentifier, fieldPositionId, urlObjects, onChange) => {

  const nextDoc = { ...doc, fields: [...doc.fields] };

  const modifiedField = getFieldByIdSelector({ fields: doc.fields, fieldId });
  let nextUrlObjects = [...urlObjects];

  /**
   * Logic below appends newly uploaded images to exisitng images if multiple images
   * upload is turned on for the image field
   */
  if (modifiedField
    && (modifiedField.type === FieldTypes.image || modifiedField.type === FieldTypes.file)
    && modifiedField.multi
    && modifiedField.value
    && modifiedField.value.length > 0) {

    nextUrlObjects = [...urlObjects, ...modifiedField.value];

  }

  if (modifiedField && modifiedField.type === FieldTypes.image && !modifiedField.multi) {
    nextUrlObjects = [urlObjects[0]];
  }

  const fieldIndex = nextDoc.fields.findIndex((field) => field._id === fieldId && field.file === fileId);

  nextDoc.fields[fieldIndex] = { ...nextDoc.fields[fieldIndex], value: nextUrlObjects };

  if (onChange) {
    onChange(
      [
        {
          sdk: 'js',
          v: 1,
          target: ChangelogTypes.fieldUpdate,
          _id: doc._id,
          identifier: doc.identifier,
          fileId,
          pageId,
          fieldPositionId,
          fieldId,
          fieldIdentifier,
          change: { value: nextUrlObjects },
          createdOn: new Date().getTime()
        }
      ],
      nextDoc
    );
  }

  return nextDoc;

};

const handleFieldDelete = (doc, view, fileId, pageId, fieldPositionId, onChange) => {

  const clonedDoc = JSON.parse(JSON.stringify(doc));
  const fileIndex = clonedDoc.files.findIndex((file) => file._id === fileId);
  const changelogs = [];

  /**
   * Mobile View Target
   */
  if (view === FileViews.mobile) {

    /**
     * Mobile View Step 1: Generate (if needed) and add mobile view to the file if mobile view is active
     */
    clonedDoc.files[fileIndex] = generateAndPopulateMobileViewForFile(clonedDoc.files[fileIndex]);

    /**
     * Mobile View Step 2: Update the layouts of only the target view
     */
    const mobileViewIndex = clonedDoc.files[fileIndex].views.findIndex((view) => view.type === FileViews.mobile);
    const mobileViewPageIndex = clonedDoc.files[fileIndex].views[mobileViewIndex].pages.findIndex((page) => pageId === page._id);
    const mobileViewTargetFieldPositionIndex = clonedDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions.findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);

    clonedDoc.files[fileIndex].views[mobileViewIndex].pages[mobileViewPageIndex].fieldPositions.splice(mobileViewTargetFieldPositionIndex, 1);

  }

  /**
   * Header and Footer Target
   */
  else if (pageId === HeaderTarget || pageId === FooterTarget) {

    //Renamed for clarity
    const targetHeaderFooterKey = pageId;

    const pageIndex = clonedDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);

    const targetFieldPositionIndex = clonedDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions.findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);
    clonedDoc.files[fileIndex][targetHeaderFooterKey].fieldPositions.splice(targetFieldPositionIndex, 1);

  }

  /**
   * Primary View Target
   */
  else {

    const pageIndex = clonedDoc.files[fileIndex].pages.findIndex((page) => pageId === page._id);

    const targetFieldPositionIndex = clonedDoc.files[fileIndex].pages[pageIndex].fieldPositions.findIndex((fieldPosition) => fieldPosition._id === fieldPositionId);
    clonedDoc.files[fileIndex].pages[pageIndex].fieldPositions.splice(targetFieldPositionIndex, 1);

  }

  changelogs.push(
    {
      sdk: 'js',
      v: 1,
      view,
      target: ChangelogTypes.fieldPositionDelete,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      pageId,
      fieldPositionId,
      createdOn: new Date().getTime()
    },
  );

  const { deletedFields, nextDoc } = removeOrphanedFieldsFromJoydoc(clonedDoc);

  if (deletedFields && deletedFields.length > 0) {

    deletedFields.forEach((field) => {

      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.fieldDelete,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        fieldId: field._id,
        createdOn: new Date().getTime()
      });

    });

  }

  if (onChange) {
    onChange(changelogs, nextDoc);
  }

  return nextDoc;

};

/**
 * >>>>>>>>>>>>> Helper Selectors <<<<<<<<<<<<<
 */
/**
 * @private
 */
const getFieldLookupSelector = createSelector(
  (state) => state.fields,
  (fields) => {
    const lookup = {};
    fields.forEach((field) => lookup[field._id] = field);
    return lookup;
  }
);

const getFieldByIdSelector = createSelector(
  getFieldLookupSelector,
  (state) => state.fieldId,
  (lookup, fieldId) => {
    return lookup[fieldId];
  }
);



export default JoyDoc;
